import type { ChatMessages, Features, Scope } from "@storytell/cp-client";
import type { UserId } from "@storytell/types";
import type { PmDoc } from "@storytell/types/richText";
import type { SetStoreFunction } from "solid-js/store";
import type { AggregatedMetrics } from "../types";

// Streaming statuses used to track each message's status
// The status of the chat is computed based on the status of all messages in the chat
// We can't rely on the last message in case the user interrupts the chat and there's
// 2 parallel operations running.
export enum StreamingMessageStatus {
  GENERATING = "GENERATING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}
// The chat is either idle or in progress
// We don't need to track if it's completed or failed, that's handled per message
export enum StreamingChatStatus {
  IDLE = "IDLE",
  GENERATING = "GENERATING",
}

export type StreamingStore = {
  chats: StreamingChatsRecord;
  messages: StreamingMessagesRecord;
  operations: StreamingOperationsRecord;
  blocks: StreamingBlocksRecord;
  orphanedOperations: Record<StreamingMessageId, StreamingOperationId[]>;
  bufferedDeltas: Record<StreamingBlockId, ChatMessages.Message[]>;
};
export type SetStreamingStore = SetStoreFunction<StreamingStore>;

export type StreamingMessageId = `message_${string}`;
export type StreamingChatId = `chat_${string}`;
export type StreamingOperationId = `operation_${string}`;
export type StreamingBlockId = `block_${string}`;

export type StreamingChatsRecord = Record<StreamingChatId, StreamingChat | undefined>;
export type StreamingOperationsRecord = Record<StreamingOperationId, StreamingOperation | undefined>;
export type StreamingBlocksRecord = Record<StreamingBlockId, StreamingBlock | undefined>;
export type StreamingMessagesRecord = Record<StreamingMessageId, StreamingMessage | undefined>;

/**
 * Hierarchy of data:
 * ```
 * Chat
 * │ Messages
 * │ │ Prompt
 * │ │ Operations (each operation can have a task property)
 * │ │ │ Blocks
 * ```
 */
export type StreamingChat = {
  id: StreamingChatId;
  messages: StreamingMessageId[];
  aggregateMetrics: AggregatedMetrics;
  /**
   * Tristate field indicating branching state:
   *   undefined — processChatRecording has not run yet (live streaming in progress; treat optimistically as supported)
   *   false     — server confirmed this is a pre-branching-feature chat (legacy)
   *   true      — server confirmed this chat has branch history
   *
   * Only set this field from within processChatRecording.
   */
  hasBranches?: boolean;
  // Chat-level citation registries for cross-message resolution
  webSearchRegistry: Map<string, ChatMessages.WebSearchResult>;
  kbSearchRegistry: Map<string, ChatMessages.SearchResult>;
};

export const createEmptyStreamingChat = (chatId: StreamingChatId): StreamingChat => ({
  id: chatId,
  messages: [],
  aggregateMetrics: {
    ingressTokens: 0,
    egressTokens: 0,
    ingressBytes: 0,
    egressBytes: 0,
    totalCredits: 0,
    ingressCredits: 0,
    egressCredits: 0,
  },
  webSearchRegistry: new Map(),
  kbSearchRegistry: new Map(),
});

// A message is a series of operations made up of blocks
// Each opereation roughly maps to a section of the UI, e.g.: the submitted prompt, a knowledge base search, etc.
export type StreamingMessage = {
  id: StreamingMessageId;
  chatId: StreamingChatId;
  feature: Features.Feature;
  /** The model the router actually served this turn on, set even when the turn failed before any block. */
  resolvedModel?: Features.Feature;
  /** On a rerouted retry only: the model the failed turn's card promised, when the served model differs. */
  reroutePromisedModel?: Features.Feature;
  index: number;
  role: ChatMessages.Role;
  prompt: StreamingPrompt;
  operations: StreamingOperationId[];
  status: StreamingMessageStatus;
  aggregateMetrics: AggregatedMetrics;
  createdBy: UserId;
  createdByEmail?: string;
  submittedAt?: Date;
  sourceChatId?: StreamingChatId;
  sourceMessageId?: StreamingMessageId;
  parentMessageId?: StreamingMessageId;
  loadError?: boolean;
};

export type StreamingPrompt = {
  messageId: StreamingMessageId;
  blockId: StreamingBlockId;
  operationId: StreamingOperationId;
  text: string;
  richText?: PmDoc;
  scope: Scope.Scope | undefined;
  resolvedMode?: number;
  /**
   * The picker selection the turn was submitted under. A client rehydrating its
   * picker reads this, never resolvedMode — an auto turn that classified into
   * research reports selectedMode AUTO and resolvedMode RESEARCH, and hydrating
   * from the latter would freeze the chat into research.
   */
  selectedMode?: number;
  /** The tier auto wanted but could not serve; resolvedMode holds what actually ran. */
  cappedFromMode?: number;
  vendorPreference?: number;
  vendorStrategy?: number;
  /** Entitlement feature of the pinned model; UNSPECIFIED (0) means no pin. */
  preferredModel?: number;
  suggestionAttribution?: {
    persona: string;
    category: string;
  };
  /** Skill registry names for respond-as; echoed by MESSAGE_START via pre_loaded_skills. */
  preLoadedSkills?: string[];
  /** User-facing tool overrides echoed by MESSAGE_START via disabled_tools. Fields set to true mean disabled. */
  disabledTools?: { webSearch?: boolean };
  attachments?: StreamingPromptAttachment[];
};

/** addToKnowledge is what the user asked for; whether it was actually added is read from the artifact. */
export type StreamingPromptAttachment = {
  artifactId: string;
  mimeType: string;
  name: string;
  addToKnowledge: boolean;
};

export type StreamingOperation = {
  index: number;
  isComplete: boolean;
  operationId: StreamingOperationId;
  messageId: StreamingMessageId;
  blocks: StreamingBlockId[];
  name: string;
  activity: string;
  startedAt: Date | undefined;
  role: ChatMessages.Role;
  status: ChatMessages.OperationStatus;
  completedAt: Date | undefined;
  task: StreamingTask | undefined;
  progressMessage: string | undefined;
  parentOperationId: StreamingOperationId | undefined;
  // Set when the operation failed due to an internal error (e.g., tool auto-activation).
  // Operations with this flag should be hidden from the UI.
  hasInternalError?: boolean;
};

export type StreamingBlockMetrics = {
  ingressTokens: number;
  egressTokens: number;
  ingressBytes: number;
  egressBytes: number;
  ingressCredits?: number;
  egressCredits?: number;
};

export type StructuredSearchResult = {
  assetName: string;
  markdownTable?: string;
  headers?: string[];
  rows?: string[][];
  rowCount?: number;
  isCompletion?: boolean;
  duration?: number;
  assetCount?: number;
  // Explains why this SQL query was chosen
  queryReason?: string;
  // The natural language query that generated these results
  query?: string;
};

export type FinalContent =
  | { type: "text"; text: string }
  | { type: "richText"; richText: PmDoc }
  | { type: "retry"; message: string }
  | {
      type: "error";
      displayMessage: string;
      systemDetail?: string;
      reason?: string;
      isFatal?: boolean;
      /** The router's would-be pick for a retry of this turn; a display hint, UNSPECIFIED when uncomputed. */
      suggestedRetryModel?: Features.Feature;
    };

export type StreamingBlock = {
  type: "block";
  isComplete: boolean;
  blockId: StreamingBlockId;
  operationId: StreamingOperationId;
  messageId: StreamingMessageId;
  role: ChatMessages.Role;
  activity: string | undefined;
  feature: Features.Feature;
  messageType: ChatMessages.MessageType;
  title: string | undefined;
  finalContent: FinalContent | undefined;
  searchResults: ChatMessages.SearchResult | undefined;
  webSearchResults: ChatMessages.WebSearchResult | undefined;
  imageResults: ChatMessages.ImageResult | undefined;
  conceptResults: ChatMessages.ConceptResult | undefined;
  structuredResults: StructuredSearchResult | undefined;
  metrics: StreamingBlockMetrics | undefined;
};

export type StreamingTask = {
  taskName: string;
  currentActivity: string | undefined;
};

export function isTaskOperation(operation: StreamingOperation): boolean {
  return operation.activity?.startsWith("agent_toolu_") || operation.name?.startsWith("Agent:") || false;
}

export function isNestedOperation(operation: StreamingOperation): boolean {
  return !!operation.parentOperationId && operation.parentOperationId.length > 0;
}
